import { CSSProperties } from 'react';

type Variant = 'icon' | 'icon-light' | 'icon-mono';

type Props = {
  size?: number;
  variant?: Variant;
  className?: string;
  title?: string;
  style?: CSSProperties;
};

/**
 * Spaciora brand mark — inline SVG so it has no asset-loading flash.
 * Floor-plan composition; amber room is the active-state hook.
 *
 * Use the simplified mark below 32px (it survives at favicon scale).
 */
export function SpacioraLogo({
  size = 32,
  variant = 'icon',
  className,
  title = 'Spaciora',
  style,
}: Props) {
  const small = size < 32;

  if (small) {
    return (
      <svg
        viewBox="0 0 32 32"
        width={size}
        height={size}
        role="img"
        aria-label={title}
        className={className}
        style={style}
      >
        <title>{title}</title>
        <rect width="32" height="32" rx="7" fill="#3D2C8D" />
        <rect x="5" y="5" width="13" height="11" rx="2" fill="#FAFAFC" />
        <rect x="20" y="5" width="7" height="11" rx="2" fill="#F4B53F" />
        <rect x="5" y="18" width="22" height="9" rx="2" fill="#FAFAFC" />
      </svg>
    );
  }

  if (variant === 'icon-mono') {
    return (
      <svg
        viewBox="0 0 256 256"
        width={size}
        height={size}
        role="img"
        aria-label={title}
        className={className}
        style={style}
      >
        <title>{title}</title>
        <rect x="3" y="3" width="250" height="250" rx="53.5" fill="none" stroke="currentColor" strokeWidth="6" />
        <g fill="currentColor">
          <rect x="32" y="32" width="92" height="84" rx="10" />
          <rect x="132" y="32" width="92" height="36" rx="10" opacity="0.65" />
          <rect x="156" y="76" width="68" height="40" rx="10" opacity="0.4" />
          <rect x="32" y="124" width="60" height="100" rx="10" opacity="0.65" />
          <rect x="100" y="124" width="124" height="100" rx="10" />
        </g>
      </svg>
    );
  }

  const isLight = variant === 'icon-light';
  const plate = isLight ? '#1A1230' : '#FAFAFC';
  const border = isLight ? '#9C8FE0' : '#3D2C8D';
  const big1 = isLight ? '#9C8FE0' : '#3D2C8D';
  const mid = '#5A47B8';
  const amber = '#F4B53F';
  const dot = '#1A1230';

  return (
    <svg
      viewBox="0 0 256 256"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      className={className}
      style={style}
    >
      <title>{title}</title>
      <rect x="0" y="0" width="256" height="256" rx="56" fill={plate} />
      <rect x="3" y="3" width="250" height="250" rx="53.5" fill="none" stroke={border} strokeWidth="6" />
      <rect x="32" y="32" width="92" height="84" rx="10" fill={big1} />
      <rect x="132" y="32" width="92" height="36" rx="10" fill={mid} />
      <rect x="156" y="76" width="68" height="40" rx="10" fill={amber} />
      <rect x="32" y="124" width="60" height="100" rx="10" fill={mid} />
      <rect x="100" y="124" width="124" height="100" rx="10" fill={big1} />
      <circle cx="190" cy="96" r="4" fill={dot} opacity="0.85" />
    </svg>
  );
}

export default SpacioraLogo;
